import * as React from "react";
import { Form } from 'react-final-form';
import { makeStyles } from "@material-ui/core/styles";
import { Box, Button, Card, CircularProgress, Theme, Typography } from "@material-ui/core";
import {
    maxLength,
    minLength,
    PasswordInput,
    required,
    Toolbar,
    useNotify,
    useRedirect,
} from "react-admin";
import StandardApiError from "shared/errors/standardApiError";
import { useSafeSetState } from "ra-core";
import { useTranslation } from "react-i18next";
import { mutateChangePassword } from "api/mutations/changePassword";

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        marginTop: '1em',
    },
    icon: {
        marginRight: theme.spacing(1),
    },
}));

const ChangePassword = (props: {}) => {
    const classes = useStyles(props);
    const [saving, setSaving] = useSafeSetState(false);
    const notify = useNotify();
    const redirect = useRedirect();
    const { t } = useTranslation();

    const save = React.useCallback(
        async (values) => {
            try {
                setSaving(true);
                await mutateChangePassword(values);
                setSaving(false);
                notify(t('changePassword.passwordChanged'), {type: 'success'});
                redirect('/login');
            } catch (error) {
                setSaving(false);
                if (error instanceof StandardApiError) {
                    error.requestErrors.forEach(error => notify(error, {type: 'error'}));
                    return error.validationErrors;
                }
            }
        },
        [setSaving, notify, redirect, t]
    );

    const formValidator = (values: any) => {
        const errors: Record<string, string> = {};

        if (values.newPassword !== values.verifyPassword) {
            errors.verifyPassword = t('changePassword.passwordsDontMatch');
        }

        return errors;
    }

    return (
        <Card className={classes.card}>
            <Form
                validate={formValidator}
                onSubmit={save}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} noValidate>
                        <Box pt="1em" pl="1em" pr="1em">
                            <Typography variant="h5" gutterBottom>
                                {t('changePassword.changePassword')}
                            </Typography>

                            <PasswordInput
                                label={t('changePassword.oldPassword')}
                                source="oldPassword"
                                validate={[required(), minLength(5), maxLength(20)]}
                                fullWidth
                                disabled={saving}
                            />

                            <PasswordInput
                                label={t('changePassword.newPassword')}
                                source="newPassword"
                                validate={[required(), minLength(5), maxLength(20)]}
                                fullWidth
                                disabled={saving}
                            />
                            <PasswordInput
                                label={t('changePassword.verifyPassword')}
                                source="verifyPassword"
                                validate={[required()]}
                                fullWidth
                                disabled={saving}
                            />
                        </Box>
                        <Toolbar>
                            <Button
                                variant="contained"
                                type="submit"
                                color="primary"
                                disabled={saving}
                            >
                                {saving && (
                                    <CircularProgress
                                    className={classes.icon}
                                        size={18}
                                        thickness={2}
                                    />
                                )}
                                {t('changePassword.changePassword')}
                            </Button>
                        </Toolbar>
                    </form>
                )}
            />
        </Card>
    );
}

export default ChangePassword;